import type { FurnitureObject, PanelPart, TextureTransform, Vec3 } from '../types';
import { defaultWardrobeShelfHeight } from './wardrobeConfig';

export const defaultTextureTransform = (): TextureTransform => ({ rotation: 0, scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0, flipX: false, flipY: false, grainDirection: 'vertical' });

const part = (
  item: FurnitureObject,
  type: PanelPart['type'],
  name: string,
  size: Vec3,
  position: Vec3,
  materialId = item.materialId,
  suffix = '',
  details: Partial<Pick<PanelPart, 'rotation' | 'profileTop' | 'drawerTravel'>> = {},
): PanelPart => {
  const id = `${item.id}-${type}-${suffix || name}`;
  const override = item.panelOverrides?.[id];
  return ({
  id,
  furnitureId: item.id,
  furnitureName: item.name,
  type,
  name,
  length: [...size].sort((a, b) => b - a)[0],
  width: [...size].sort((a, b) => b - a)[1],
  thickness: [...size].sort((a, b) => b - a)[2],
  materialId: override?.materialId ?? materialId,
  texture: override?.texture ?? defaultTextureTransform(),
  quantity: 1,
    position,
    size,
    ...details,
});
};

export function generateCabinetParts(item: FurnitureObject): PanelPart[] {
  const { width: w, height: h, depth: d, panelThickness: t, backThickness: bt } = item;
  const innerWidth = Math.max(1, w - t * 2);
  const parts: PanelPart[] = [
    part(item, 'left', 'Left side', [t, h, d], [-w / 2 + t / 2, h / 2, 0]),
    part(item, 'right', 'Right side', [t, h, d], [w / 2 - t / 2, h / 2, 0]),
    part(item, 'bottom', 'Bottom', [innerWidth, t, d], [0, t / 2, 0]),
    part(item, 'top', 'Top', [innerWidth, t, d], [0, h - t / 2, 0]),
  ];
  if (item.backEnabled !== false) parts.push(part(item, 'back', 'Back', [innerWidth, Math.max(1, h - t * 2), bt], [0, h / 2, -d / 2 + bt / 2], item.backMaterialId));

  for (let i = 0; i < item.shelfCount; i += 1) {
    const y = t + ((h - t * 2) * (i + 1)) / (item.shelfCount + 1);
    const backInset = item.backEnabled === false ? 0 : bt;
    parts.push(part(item, 'shelf', `Shelf ${i + 1}`, [innerWidth, t, Math.max(1, d - backInset)], [0, y, backInset / 2], item.materialId, String(i)));
  }

  if (item.doors.enabled && item.doors.count > 0) {
    const count = item.doors.count;
    const doorWidth = Math.max(1, (w - item.doors.gap * (count + 1)) / count);
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + item.doors.gap + doorWidth / 2 + i * (doorWidth + item.doors.gap);
      const track = item.doors.opening === 'sliding' ? i * (item.doors.thickness + 3) : 0;
      parts.push(part(item, 'door', `Door ${i + 1}`, [doorWidth, h - item.doors.gap * 2, item.doors.thickness], [x, h / 2, d / 2 + item.doors.thickness / 2 + track], item.doors.materialId ?? item.materialId, String(i)));
    }
  }

  for (let i = 0; i < item.drawers.count; i += 1) {
    const y = item.drawers.gap + item.drawers.frontHeight / 2 + i * (item.drawers.frontHeight + item.drawers.gap);
    if (y + item.drawers.frontHeight / 2 >= h) break;
    const frontZ = item.doors.enabled && item.doors.count > 0 ? d / 2 - t / 2 - Math.max(2, item.doors.gap) : d / 2 + t / 2;
    parts.push(part(item, 'drawerFront', `Drawer front ${i + 1}`, [w - item.drawers.gap * 2, item.drawers.frontHeight, t], [0, y, frontZ], item.materialId, String(i)));
  }

  return parts;
}

export function generateWardrobeParts(item: FurnitureObject): PanelPart[] {
  const { width: w, depth: d, panelThickness: t, backThickness: bt } = item;
  const left = item.slope?.leftHeight ?? item.height;
  const right = item.slope?.rightHeight ?? item.height;
  const heightAt = (x: number) => left + (right - left) * (x + w / 2) / w;
  const innerWidth = Math.max(1, w - 2 * t);
  const backInset = item.backEnabled === false ? 0 : bt;
  const topLeft = heightAt(-w / 2 + t);
  const topRight = heightAt(w / 2 - t);
  const topAngle = Math.atan2(topRight - topLeft, innerWidth);
  const parts: PanelPart[] = [
    part(item, 'left', 'Left side', [t, left, d], [-w / 2 + t / 2, left / 2, 0]),
    part(item, 'right', 'Right side', [t, right, d], [w / 2 - t / 2, right / 2, 0]),
    part(item, 'bottom', 'Bottom', [innerWidth, t, d], [0, t / 2, 0]),
    part(item, 'top', 'Sloped top', [Math.hypot(innerWidth, topRight - topLeft), t, d], [0, (topLeft + topRight) / 2 - t / 2, 0], item.materialId, '', { rotation: [0, 0, topAngle] }),
  ];
  if (item.backEnabled !== false) {
    const l = Math.max(1, topLeft - 2 * t), r = Math.max(1, topRight - 2 * t);
    const height = Math.max(l, r);
    parts.push(part(item, 'back', 'Back', [innerWidth, height, bt], [0, t + height / 2, -d / 2 + bt / 2], item.backMaterialId, '', Math.abs(l - r) > .01 ? { profileTop: [l, r] } : {}));
  }
  let cursor = -item.width / 2;
  item.sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      const dividerHeight = Math.max(1, heightAt(cursor) - 2 * t);
      parts.push(part(item, 'divider', `Divider ${sectionIndex}`, [t, dividerHeight, d - backInset], [cursor, t + dividerHeight / 2, backInset / 2], item.materialId, String(sectionIndex)));
    }
    const center = cursor + section.width / 2;
    const sectionTop = Math.min(heightAt(cursor), heightAt(cursor + section.width)) - 2 * t;
    const clearWidth = Math.max(1, section.width - t);
    const shelfThickness = section.shelfThickness ?? t;
    for (let i = 0; i < (item.intermediateTopCount ?? 0); i += 1) {
      const y = section.intermediateHeights?.[i] ?? t + sectionTop * (i + 1) / ((item.intermediateTopCount ?? 0) + 1);
      parts.push(part(item, 'intermediate', `Section ${sectionIndex + 1} intermediate ${i + 1}`, [clearWidth, t, d - backInset], [center, y, backInset / 2], item.materialId, `${sectionIndex}-${i}`));
    }
    const drawerHeights = Array.from({ length: section.drawerCount }, (_, index) => section.drawerHeights?.[index] ?? item.drawers.frontHeight);
    const drawerStack = drawerHeights.reduce((sum, value) => sum + value + item.drawers.gap, 0);
    if (section.shelfAboveDrawers && section.drawerCount > 0) parts.push(part(item, 'shelf', `Section ${sectionIndex + 1} above drawers`, [clearWidth, shelfThickness, d - backInset], [center, t + drawerStack + shelfThickness / 2, backInset / 2], item.materialId, `${sectionIndex}-above-drawers`));
    for (let i = 0; i < section.shelfCount; i += 1) {
      const y = section.shelfHeights?.[i] ?? defaultWardrobeShelfHeight(item, sectionIndex, i);
      parts.push(part(item, 'shelf', `Section ${sectionIndex + 1} shelf ${i + 1}`, [clearWidth, shelfThickness, d - backInset], [center, y, backInset / 2], item.materialId, `${sectionIndex}-${i}`));
    }
    if (section.hangingRail) {
      parts.push(part(item, 'rail', `Section ${sectionIndex + 1} hanging rail`, [clearWidth, 25, 25], [center, section.railHeightFromFloor ?? item.height * .72, d * .15], 'black', `${sectionIndex}`));
    }
    let drawerBottom = t;
    for (let i = 0; i < section.drawerCount; i += 1) {
      const frontHeight = drawerHeights[i];
      const frontWidth = Math.max(1, clearWidth - item.drawers.gap * 2);
      const depth = Math.min(d - backInset - t, section.drawerDepth ?? item.drawers.depth);
      const travel = depth * Math.min(75, section.drawerExtension ?? 75) / 100;
      const y = drawerBottom + item.drawers.gap + frontHeight / 2;
      drawerBottom += item.drawers.gap + frontHeight;
      const hasDoors = item.doors.enabled && item.doors.count > 0;
      const interiorFront = d / 2 - (hasDoors ? Math.max(2, item.doors.gap) : 0);
      const z = interiorFront - depth / 2;
      const suffix = `${sectionIndex}-${i}`;
      parts.push(part(item, 'drawerFront', `Section ${sectionIndex + 1} drawer front ${i + 1}`, [frontWidth, frontHeight, t], [center, y, hasDoors ? interiorFront - t / 2 : interiorFront + t / 2], item.materialId, suffix, { drawerTravel: travel }));
      for (const side of [-1, 1]) parts.push(part(item, 'drawerSide', `Section ${sectionIndex + 1} drawer ${i + 1} side ${side}`, [t, Math.max(1, frontHeight - t), depth], [center + side * (frontWidth / 2 - t / 2), y, z], item.materialId, `${suffix}-${side}`, { drawerTravel: travel }));
      parts.push(part(item, 'drawerBottom', `Section ${sectionIndex + 1} drawer ${i + 1} bottom`, [Math.max(1, frontWidth - 2 * t), t, depth], [center, y - frontHeight / 2 + t / 2, z], item.materialId, suffix, { drawerTravel: travel }));
    }
    cursor += section.width;
  });
  if (item.doors.enabled && item.doors.count > 0) {
    const count = item.doors.count;
    const gap = item.doors.gap;
    const doorWidth = Math.max(1, (w - gap * (count + 1)) / count);
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + gap + doorWidth / 2 + i * (doorWidth + gap);
      const leftTop = heightAt(x - doorWidth / 2) - 2 * gap;
      const rightTop = heightAt(x + doorWidth / 2) - 2 * gap;
      const doorHeight = Math.max(leftTop, rightTop);
      const track = item.doors.opening === 'sliding' ? i * (item.doors.thickness + 3) : 0;
      parts.push(part(item, 'door', `Door ${i + 1}`, [doorWidth, doorHeight, item.doors.thickness], [x, gap + doorHeight / 2, d / 2 + item.doors.thickness / 2 + track], item.doors.materialId ?? item.materialId, String(i), Math.abs(leftTop - rightTop) > .01 ? { profileTop: [leftTop, rightTop] } : {}));
    }
  }
  return parts;
}

export const generateFurnitureParts = (item: FurnitureObject) =>
  item.kind === 'wardrobe' ? generateWardrobeParts(item) : generateCabinetParts(item);

export const generateCuttingList = (items: FurnitureObject[]) => items.flatMap(generateFurnitureParts);
